import { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, Pencil, Trash, Copy, Eye, X, CheckCircle, Circle, ClockClockwise, BookOpen } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PRIORITY_LABELS = { 1: "P1 · High", 2: "P2 · Normal", 3: "P3 · Low" };
const TOKEN_WARN_THRESHOLD = 30000;   // warn the admin if KB exceeds ~30k tokens

const blankArticle = () => ({
  id: null, title: "", category: "products", body: "", priority: 2, is_active: true, tags: []
});

export default function KnowledgeBaseTab() {
  const { fetchWithAuth } = useAuth();
  const [articles, setArticles]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState({ category: "", active: "", q: "" });
  const [drawer, setDrawer]       = useState(null);     // open article for edit/create
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditEntries, setAudit]  = useState([]);
  const [previewOpen, setPreview] = useState(false);
  const [previewText, setPreviewText] = useState("");
  const [saving, setSaving]       = useState(false);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filter.category) params.set("category", filter.category);
    if (filter.active)   params.set("active", filter.active);
    if (filter.q)        params.set("q", filter.q);
    const res = await fetchWithAuth(`${API_URL}/api/kb${params.toString() ? "?" + params : ""}`);
    if (!res.ok) { toast.error(res.data?.error || "Failed to load articles"); return; }
    setArticles(res.data.articles);
    setCategories(res.data.categories);
    setStats(res.data.stats);
    setLoading(false);
  }, [fetchWithAuth, filter]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const buckets = {};
    for (const a of articles) {
      if (!buckets[a.category]) buckets[a.category] = [];
      buckets[a.category].push(a);
    }
    return buckets;
  }, [articles]);

  const openCreate = () => setDrawer(blankArticle());
  const openEdit = (a) => setDrawer({ ...a });

  const save = async () => {
    if (!drawer.title || !drawer.body) { toast.error("Title and body are required"); return; }
    setSaving(true);
    try {
      const url = drawer.id ? `${API_URL}/api/kb/${drawer.id}` : `${API_URL}/api/kb`;
      const method = drawer.id ? "PUT" : "POST";
      const res = await fetchWithAuth(url, { method, data: drawer });
      if (!res.ok) { toast.error(res.data?.error || "Save failed"); return; }
      toast.success(`Article ${drawer.id ? "updated" : "created"}. Arya picks it up on the next chat reply.`);
      setDrawer(null);
      await load();
    } finally { setSaving(false); }
  };

  const toggleActive = async (a) => {
    const res = await fetchWithAuth(`${API_URL}/api/kb/${a.id}`, {
      method: "PUT", data: { is_active: !a.is_active }
    });
    if (!res.ok) toast.error(res.data?.error || "Toggle failed");
    else { toast.success(`Article ${a.is_active ? "deactivated" : "activated"}`); load(); }
  };

  const duplicate = async (a) => {
    const res = await fetchWithAuth(`${API_URL}/api/kb/${a.id}/duplicate`, { method: "POST" });
    if (!res.ok) toast.error(res.data?.error || "Duplicate failed");
    else { toast.success("Duplicated as an inactive draft"); load(); }
  };

  const hardDelete = async (a) => {
    if (!window.confirm(`Permanently delete "${a.title}"? This cannot be undone. Consider deactivating instead.`)) return;
    const res = await fetchWithAuth(`${API_URL}/api/kb/${a.id}`, { method: "DELETE" });
    if (!res.ok) toast.error(res.data?.error || "Delete failed");
    else { toast.success("Article deleted"); load(); }
  };

  const showPreview = async () => {
    const res = await fetchWithAuth(`${API_URL}/api/kb/preview-prompt`);
    if (!res.ok) { toast.error(res.data?.error || "Preview failed"); return; }
    setPreviewText(res.data.content || "(empty — no active articles)");
    setPreview(true);
  };

  const showAudit = async () => {
    const res = await fetchWithAuth(`${API_URL}/api/kb/audit`);
    if (!res.ok) { toast.error(res.data?.error || "Audit fetch failed"); return; }
    setAudit(res.data.entries);
    setAuditOpen(true);
  };

  const tokensOverThreshold = stats && stats.estimated_tokens > TOKEN_WARN_THRESHOLD;

  if (loading) return <div className="text-[#666] text-sm py-12 text-center" data-testid="kb-loading">Loading Knowledge Base…</div>;

  return (
    <div className="space-y-5" data-testid="knowledge-base-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
            <BookOpen size={20} weight="duotone" className="text-[#E85D24]" />
            Knowledge Base
          </h2>
          <p className="text-sm text-[#888] mt-1.5 max-w-2xl">
            Articles below are concatenated and injected into Arya&apos;s system prompt as authoritative ground truth. Changes go live on the next chat reply — no redeploy.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={showAudit} data-testid="kb-audit-btn"
            className="px-3 py-1.5 rounded text-xs font-medium border border-[#2A2A2A] text-[#888] hover:border-[#444] hover:text-white transition-colors">
            <ClockClockwise size={12} weight="bold" className="inline mr-1 -mt-0.5" />Audit log
          </button>
          <button onClick={showPreview} data-testid="kb-preview-btn"
            className="px-3 py-1.5 rounded text-xs font-medium border border-[#2A2A2A] text-[#888] hover:border-[#444] hover:text-white transition-colors">
            <Eye size={12} weight="bold" className="inline mr-1 -mt-0.5" />Preview prompt block
          </button>
          <button onClick={openCreate} data-testid="kb-new-btn"
            className="px-4 py-1.5 rounded text-xs font-bold bg-[#E85D24] text-white hover:bg-[#FF6A2C] transition-colors">
            <Plus size={12} weight="bold" className="inline mr-1 -mt-0.5" />New article
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className={`grid grid-cols-4 gap-3 p-4 rounded-lg border ${tokensOverThreshold ? "border-[#FF3B30]/40 bg-[#1A0808]/40" : "border-[#1A1A1A] bg-[#0A0A0A]"}`}>
        <StatCell label="Total articles" value={articles.length} />
        <StatCell label="Active" value={articles.filter(a => a.is_active).length} accent="#34C759" />
        <StatCell label="Total characters" value={stats?.total_chars?.toLocaleString() || "—"} />
        <StatCell label="Est. tokens injected" value={stats?.estimated_tokens?.toLocaleString() || "—"}
          accent={tokensOverThreshold ? "#FF3B30" : "#E85D24"}
          subtitle={tokensOverThreshold ? `Above ${TOKEN_WARN_THRESHOLD.toLocaleString()} — consider trimming` : "Healthy"} />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
          <input
            data-testid="kb-search" type="text" placeholder="Search title or body..."
            value={filter.q}
            onChange={(e) => setFilter(f => ({ ...f, q: e.target.value }))}
            className="w-full pl-9 pr-3 py-2 bg-[#0A0A0A] border border-[#222] rounded text-sm text-white placeholder-[#555] focus:outline-none focus:border-[#E85D24]/50"
          />
        </div>
        <select value={filter.category} onChange={(e) => setFilter(f => ({ ...f, category: e.target.value }))}
          data-testid="kb-filter-category"
          className="px-3 py-2 bg-[#0A0A0A] border border-[#222] rounded text-sm text-white">
          <option value="">All categories</option>
          {categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <select value={filter.active} onChange={(e) => setFilter(f => ({ ...f, active: e.target.value }))}
          data-testid="kb-filter-active"
          className="px-3 py-2 bg-[#0A0A0A] border border-[#222] rounded text-sm text-white">
          <option value="">All states</option>
          <option value="true">Active only</option>
          <option value="false">Inactive only</option>
        </select>
      </div>

      {/* Article list, grouped by category */}
      <div className="border border-[#1A1A1A] rounded-lg overflow-hidden">
        {categories.map(cat => {
          const items = grouped[cat.key] || [];
          if (items.length === 0 && (filter.category || filter.q || filter.active)) return null;
          return (
            <div key={cat.key}>
              <div className="px-5 py-2 bg-[#080808] text-[10px] uppercase tracking-wider text-[#999] font-bold border-b border-[#151515] flex items-center justify-between">
                <span>{cat.label}</span>
                <span className="text-[#555]">{items.length} article{items.length !== 1 ? "s" : ""}</span>
              </div>
              {items.length === 0 ? (
                <div className="px-5 py-4 text-xs text-[#555] italic">No articles in this category yet.</div>
              ) : items.map(a => (
                <div key={a.id} data-testid={`kb-row-${a.id}`}
                  className="grid grid-cols-[1fr_100px_140px_120px] gap-3 px-5 py-3 border-b border-[#121212] last:border-b-0 items-center hover:bg-[#0A0A0A]/50">
                  <div className="min-w-0">
                    <button onClick={() => openEdit(a)} className="text-sm font-medium text-white truncate text-left hover:text-[#E85D24] transition-colors">
                      {a.title}
                    </button>
                    <p className="text-[10px] text-[#666] mt-0.5 line-clamp-1">{a.body.replace(/[*#_`]/g, "").slice(0, 120)}…</p>
                  </div>
                  <div className="text-[11px] text-[#888]">{PRIORITY_LABELS[a.priority]}</div>
                  <button onClick={() => toggleActive(a)}
                    data-testid={`kb-toggle-${a.id}`}
                    className={`flex items-center gap-1.5 text-[11px] font-semibold transition-colors ${
                      a.is_active ? "text-[#34C759]" : "text-[#555] hover:text-[#888]"}`}>
                    {a.is_active
                      ? <CheckCircle size={12} weight="fill" />
                      : <Circle size={12} weight="bold" />}
                    {a.is_active ? "Active" : "Inactive"}
                  </button>
                  <div className="flex items-center justify-end gap-1">
                    <IconBtn label="Edit"      icon={Pencil}    onClick={() => openEdit(a)}   testid={`kb-edit-${a.id}`} />
                    <IconBtn label="Duplicate" icon={Copy}      onClick={() => duplicate(a)}  testid={`kb-dup-${a.id}`} />
                    <IconBtn label="Delete"    icon={Trash}     onClick={() => hardDelete(a)} testid={`kb-del-${a.id}`} danger />
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Edit drawer */}
      {drawer && (
        <DrawerEditor article={drawer} setArticle={setDrawer} categories={categories}
          onSave={save} onClose={() => setDrawer(null)} saving={saving} />
      )}

      {/* Preview modal */}
      {previewOpen && (
        <Modal title="Prompt block injected into Arya" onClose={() => setPreview(false)}>
          <pre className="text-xs text-[#ccc] bg-[#050505] p-4 rounded border border-[#1A1A1A] whitespace-pre-wrap font-mono leading-relaxed max-h-[60vh] overflow-y-auto" data-testid="kb-preview-content">
            {previewText}
          </pre>
        </Modal>
      )}

      {/* Audit modal */}
      {auditOpen && (
        <Modal title="Audit log (last 50 changes)" onClose={() => setAuditOpen(false)}>
          {auditEntries.length === 0 ? (
            <p className="text-sm text-[#666] py-8 text-center">No edits recorded yet.</p>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {auditEntries.map(e => (
                <div key={e.id} className="border border-[#1A1A1A] rounded p-3 text-xs">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white font-medium">{e.article_title || "(deleted)"}</span>
                    <span className="text-[10px] text-[#666]">{new Date(e.at).toLocaleString()}</span>
                  </div>
                  <div className="text-[11px] text-[#888]"><strong className="text-[#E85D24]">{e.action}</strong> by {e.actor}</div>
                  {e.diff_summary && <div className="text-[10px] text-[#666] mt-1 font-mono">{e.diff_summary}</div>}
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// === Sub-components ===
function StatCell({ label, value, subtitle, accent = "#fff" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#666] font-semibold mb-1">{label}</div>
      <div className="text-xl font-bold" style={{ color: accent }}>{value}</div>
      {subtitle && <div className="text-[10px] text-[#888] mt-0.5">{subtitle}</div>}
    </div>
  );
}

function IconBtn({ icon: Icon, label, onClick, testid, danger }) {
  return (
    <button onClick={onClick} title={label} data-testid={testid}
      className={`p-1.5 rounded transition-colors ${danger ? "text-[#666] hover:text-[#FF3B30] hover:bg-[#1A0808]" : "text-[#666] hover:text-white hover:bg-[#1A1A1A]"}`}>
      <Icon size={14} weight="bold" />
    </button>
  );
}

function DrawerEditor({ article, setArticle, categories, onSave, onClose, saving }) {
  // Split-pane editor: markdown source on the left, rendered HTML preview on the right.
  // Uses a tiny custom renderer (no extra deps) — handles **bold**, *italics*, headings, lists, links, code spans.
  const html = useMemo(() => renderMarkdown(article.body || ""), [article.body]);
  const charCount = (article.body || "").length;
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-stretch justify-end" onClick={onClose} data-testid="kb-drawer">
      <div className="w-[820px] max-w-full bg-[#0A0A0A] border-l border-[#222] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="p-5 border-b border-[#1A1A1A] flex items-center justify-between">
          <h3 className="text-base font-bold text-white">{article.id ? "Edit article" : "New article"}</h3>
          <button onClick={onClose} className="text-[#888] hover:text-white p-1"><X size={16} /></button>
        </header>
        <div className="p-5 space-y-3 flex-1 overflow-y-auto">
          <Field label="Title">
            <input data-testid="kb-input-title" type="text" value={article.title} maxLength={200}
              onChange={(e) => setArticle(a => ({ ...a, title: e.target.value }))}
              className="w-full bg-[#0A0A0A] border border-[#222] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#E85D24]/50" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <select data-testid="kb-input-category" value={article.category}
                onChange={(e) => setArticle(a => ({ ...a, category: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-[#222] rounded px-3 py-2 text-sm text-white">
                {categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Priority (affects order in prompt)">
              <select data-testid="kb-input-priority" value={article.priority}
                onChange={(e) => setArticle(a => ({ ...a, priority: parseInt(e.target.value, 10) }))}
                className="w-full bg-[#0A0A0A] border border-[#222] rounded px-3 py-2 text-sm text-white">
                <option value={1}>P1 · High (top of prompt)</option>
                <option value={2}>P2 · Normal</option>
                <option value={3}>P3 · Low (bottom of prompt)</option>
              </select>
            </Field>
          </div>
          <Field label={<span>Body <span className="text-[#666] font-mono text-[10px] ml-2">Markdown · {charCount.toLocaleString()} chars · ~{Math.round(charCount/4).toLocaleString()} tokens</span></span>}>
            <div className="grid grid-cols-2 gap-3">
              <textarea data-testid="kb-input-body" rows={18} value={article.body}
                onChange={(e) => setArticle(a => ({ ...a, body: e.target.value }))}
                placeholder="Write in Markdown. Use **bold**, *italics*, # headings, - bullet lists, [link](url)."
                className="w-full bg-[#050505] border border-[#222] rounded px-3 py-2 text-xs text-white font-mono focus:outline-none focus:border-[#E85D24]/50 resize-none" />
              <div data-testid="kb-input-preview" className="bg-[#050505] border border-[#1A1A1A] rounded px-3 py-2 text-xs text-[#ccc] overflow-y-auto prose prose-invert prose-sm max-w-none"
                style={{ maxHeight: "420px" }}
                dangerouslySetInnerHTML={{ __html: html }} />
            </div>
          </Field>
          <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
            <input data-testid="kb-input-active" type="checkbox" checked={!!article.is_active}
              onChange={(e) => setArticle(a => ({ ...a, is_active: e.target.checked }))} />
            Active — include in Arya&apos;s prompt
          </label>
        </div>
        <footer className="p-4 border-t border-[#1A1A1A] flex justify-end gap-2 bg-[#080808]">
          <button onClick={onClose}
            className="px-4 py-2 text-xs font-medium border border-[#2A2A2A] text-[#888] hover:border-[#444] hover:text-white rounded">Cancel</button>
          <button onClick={onSave} disabled={saving} data-testid="kb-save-btn"
            className="px-4 py-2 text-xs font-bold bg-[#E85D24] text-white hover:bg-[#FF6A2C] disabled:opacity-40 rounded">
            {saving ? "Saving…" : article.id ? "Save changes" : "Create article"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-[#888] font-semibold mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#222] rounded-lg max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="p-4 border-b border-[#1A1A1A] flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">{title}</h3>
          <button onClick={onClose} className="text-[#888] hover:text-white"><X size={16} /></button>
        </header>
        <div className="p-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

// === Minimal Markdown renderer ===
// Handles headings, bold, italics, code spans, links, bullet & numbered lists, paragraphs.
// Intentionally not pulling in a full markdown lib — the dashboard's needs are simple
// and this keeps the bundle small.
function renderMarkdown(src) {
  if (!src) return "";
  const escape = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  let html = escape(src);
  // headings
  html = html.replace(/^### (.*)$/gm, '<h3 style="font-size:14px;color:#E85D24;margin:8px 0">$1</h3>');
  html = html.replace(/^## (.*)$/gm,  '<h2 style="font-size:15px;color:#fff;margin:10px 0">$1</h2>');
  html = html.replace(/^# (.*)$/gm,   '<h1 style="font-size:16px;color:#fff;margin:12px 0">$1</h1>');
  // bold and italics
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong style="color:#E85D24">$1</strong>');
  html = html.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  // code spans
  html = html.replace(/`([^`]+)`/g, '<code style="background:#1a1a1a;color:#FFD60A;padding:1px 4px;border-radius:3px;font-size:10px">$1</code>');
  // links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener" style="color:#E85D24;text-decoration:underline">$1</a>');
  // bullet & numbered lists (simple — one level)
  html = html.replace(/(^|\n)((?:- .+\n?)+)/g, (m, pre, lst) =>
    `${pre}<ul style="margin:4px 0 4px 18px">${lst.trim().split("\n").map(l => `<li>${l.replace(/^- /, "")}</li>`).join("")}</ul>`);
  html = html.replace(/(^|\n)((?:\d+\. .+\n?)+)/g, (m, pre, lst) =>
    `${pre}<ol style="margin:4px 0 4px 18px">${lst.trim().split("\n").map(l => `<li>${l.replace(/^\d+\. /, "")}</li>`).join("")}</ol>`);
  // paragraphs (double-newline)
  html = html.split(/\n\n+/).map(p => p.match(/^<(h\d|ul|ol|p)/) ? p : `<p style="margin:6px 0">${p.replace(/\n/g, "<br/>")}</p>`).join("");
  return html;
}
